/**
 * icsUtils.js — Shared ICS calendar generation for jobs
 *
 * Supports multi-day shoots, crew attendees, and download trigger.
 */

function fmtICS(dt) {
  return dt.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

function escICS(str) {
  return (str || '').replace(/,/g, '\\,').replace(/;/g, '\\;');
}

/**
 * Generate ICS content string for a job.
 *
 * @param {Object} job - Job object with shootDate, shootDates, title, location, clientName, revenue, id
 * @param {Object} [options]
 * @param {Array}  [options.attendees] - Array of { name, email } for ATTENDEE fields
 * @param {Object} [options.shootTimesMap] - { 'YYYY-MM-DD': 'HH:MM' } earliest shoot time per date
 * @param {Object} [options.shootDurMap] - { 'YYYY-MM-DD': minutes } total duration per date
 * @returns {string|null} ICS content string, or null if no dates
 */
export function generateJobICS(job, options = {}) {
  const dates = Array.isArray(job.shootDates) && job.shootDates.length > 0
    ? job.shootDates : (job.shootDate ? [job.shootDate] : []);
  if (!dates.length) return null;

  const attendeeLines = (options.attendees || [])
    .filter((a) => a.email)
    .map((a) => `ATTENDEE;CN=${escICS(a.name || a.email)};RSVP=TRUE:mailto:${a.email}`);

  const shootTimesMap = options.shootTimesMap || {};
  const shootDurMap = options.shootDurMap || {};

  const events = dates.map((sd, i) => {
    // Use actual shoot time if available, otherwise default 9 AM
    const startTime = shootTimesMap[sd] || '09:00';
    const [sh, sm] = startTime.split(':').map(Number);
    const d = new Date(sd + 'T00:00:00');
    d.setHours(sh, sm, 0, 0);

    // Use actual duration if available, otherwise default 480 min (8 hours)
    const durMins = shootDurMap[sd] || 480;
    const end = new Date(d.getTime() + durMins * 60 * 1000);

    const dayLabel = dates.length > 1 ? ` (Day ${i + 1}/${dates.length})` : '';
    const lines = [
      'BEGIN:VEVENT',
      `UID:rfos-job-${job.id}-day${i + 1}@rotoflight`,
      `DTSTART:${fmtICS(d)}`,
      `DTEND:${fmtICS(end)}`,
      `SUMMARY:${escICS(job.title || 'Job')}${dayLabel}`,
      `LOCATION:${escICS(job.location || '')}`,
      `DESCRIPTION:Client: ${escICS(job.clientName || 'N/A')}\\nRevenue: $${job.revenue || 0}`,
      ...attendeeLines,
      'END:VEVENT',
    ];
    return lines.join('\r\n');
  });

  return ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//RFOS//Job Calendar//EN', ...events, 'END:VCALENDAR'].join('\r\n');
}

/**
 * Generate and trigger download of an ICS file for a job.
 *
 * @param {Object} job
 * @param {Object} [options] - Same as generateJobICS options
 */
export function downloadJobICS(job, options = {}) {
  const content = generateJobICS(job, options);
  if (!content) return;
  const blob = new Blob([content], { type: 'text/calendar' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(job.title || 'job').replace(/\s+/g, '_')}.ics`;
  a.click();
  URL.revokeObjectURL(url);
}
